import { isValidElement } from 'react';

import { isFunction, isObject, isString } from '@toolbox/utils';
import { extendErrorMessage, validators } from '../validators';
import { getValueAndMessage } from './getValueAndMessage';

import type { ValidateFactory, ValidatorProps } from '../validators';
import type { Modify } from '@toolbox/utils';
import type {
  FieldPath,
  FieldPathValue,
  FieldValues,
  Message,
  UseControllerProps,
  Validate,
  ValidationRule,
} from 'react-hook-form';

export type UseControllerRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = NonNullable<UseControllerProps<TFieldValues, TFieldName>['rules']>;

export type UseFieldRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Modify<
  UseControllerRules<TFieldValues, TFieldName>,
  {
    phoneNumber?: Message | ValidationRule<boolean>;
    email?: Message | ValidationRule<boolean>;
    validate?:
      | ValidateFactory<FieldPathValue<TFieldValues, TFieldName>>
      | Record<string, ValidateFactory<FieldPathValue<TFieldValues, TFieldName>>>;
  }
>;

export const processRules = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  rules: UseFieldRules<TFieldValues, TFieldName>,
  props: ValidatorProps<TFieldValues>,
): UseControllerRules<TFieldValues, TFieldName> => {
  const { validate, phoneNumber, email, min, required, ...rest } = rules;
  const newRules = rest as Modify<
    UseControllerRules<TFieldValues, TFieldName>,
    {
      validate: Record<string, Validate<FieldPathValue<TFieldValues, TFieldName>>>;
    }
  >;

  if (validate) {
    if (isFunction(validate)) {
      newRules.validate = {
        default: validate(props) as any,
      };
    } else if (isObject(validate)) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const validateEntries = Object.entries(validate) as [
        string,
        ValidateFactory<FieldPathValue<TFieldValues, TFieldName>>,
      ][];
      newRules.validate = validateEntries.reduce((newValidate, [key, validate]) => {
        newValidate[key] = validate(props) as any;
        return newValidate;
      }, {} as Record<string, Validate<FieldPathValue<TFieldValues, TFieldName>>>);
    }
  } else {
    newRules.validate = {};
  }

  if (email) {
    const { value, message } =
      isString(email) || isValidElement(email)
        ? { value: !!email, message: email as Message }
        : getValueAndMessage(email);

    if (value) {
      newRules.validate.email = extendErrorMessage(
        validators.email,
        () =>
          message ||
          "Ungültiges E-Mail-Format. E-Mail muss aus einem Präfix, '@' und einer Domäne bestehen",
      )(props) as Validate<FieldPathValue<TFieldValues, TFieldName>>;
    }
  }

  if (min) {
    newRules.validate.min = extendErrorMessage(validators.min(min), () => {
      return `Dieser Wert muss gleich ${min.toString()} oder größer sein`;
    })(props) as Validate<FieldPathValue<TFieldValues, TFieldName>>;
  }

  if (required) {
    newRules.validate.required = extendErrorMessage(validators.required(required), () => {
      return 'Dies ist ein Pflichtfeld';
    })(props) as Validate<FieldPathValue<TFieldValues, TFieldName>>;
  }

  return newRules as any;
};
